import { BrokerInvoiceStatus, netTermsToDays } from '@numeo/types-core'
import type { TmsInvoiceAgingBucket, TmsInvoiceRow } from '@numeo/types-main-web-app'
import type { BadgeType } from '@/components/ds2'
import { formatUsd } from './format'

/** An invoice row's status as the user reads it: its label and the Badge tone that shows it. */
interface InvoiceStatus {
    label: string
    type: BadgeType
}

const ISSUE_STATUS: Partial<Record<BrokerInvoiceStatus, InvoiceStatus>> = {
    [BrokerInvoiceStatus.HELD]: { label: 'Held by factor', type: 'warning' },
    [BrokerInvoiceStatus.DENIED]: { label: 'Denied', type: 'danger' },
    [BrokerInvoiceStatus.DISPUTED]: { label: 'Disputed', type: 'danger' },
}

/** An invoice row's status, from its Invoices page group and, inside the groups that split, its stored status. */
export const invoiceStatusOf = (row: TmsInvoiceRow): InvoiceStatus => {
    switch (row.status_group) {
        case 'funded_by_factor':
            return { label: 'Funded by factor', type: 'success' }
        case 'paid_by_broker':
            return { label: 'Paid', type: 'success' }
        case 'issues':
            return (row.status_stored && ISSUE_STATUS[row.status_stored]) || { label: 'Needs attention', type: 'danger' }
        case 'invoiced':
            return row.status_stored === BrokerInvoiceStatus.SUBMITTED ? { label: 'Submitted', type: 'default' } : { label: 'Invoiced', type: 'accent' }
        case 'pending':
            return { label: 'Pending', type: 'default' }
        default:
            return { label: row.status_group, type: 'default' }
    }
}

/** The calendar day (UTC, YYYY-MM-DD) a date string carries. */
const dayOf = (iso: string): string => iso.slice(0, 10)

/**
 * Unpaid and past its terms: the Follow up action. A funded or paid row never gets it. Past terms is the server's word on an aged row
 * (`past_terms`); otherwise a due date that has passed, or an age at or past the threshold (the card's aged threshold, else the row's terms).
 */
export const isPastDue = (row: TmsInvoiceRow, today: Date = new Date(), thresholdDays?: number): boolean => {
    if (row.status_group === 'funded_by_factor' || row.status_group === 'paid_by_broker') return false
    if (row.past_terms === true) return true
    if (row.status_group !== 'invoiced') return false
    if (row.due_date && dayOf(row.due_date) < dayOf(today.toISOString())) return true
    const threshold = thresholdDays ?? netTermsToDays(row.payment_terms)
    return row.days_outstanding !== null && threshold !== undefined && row.days_outstanding >= threshold
}

/** A due date as "Oct 2", read on its UTC day (the stored day is UTC midnight, never shifted). */
const shortDate = (iso: string): string => new Date(`${dayOf(iso)}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })

/** The age line under an invoice: "45 days · due Oct 2", or "unpaid" when no age is stored. */
export const ageLineOf = (row: TmsInvoiceRow): string => {
    const age = row.days_outstanding === null ? 'unpaid' : `${row.days_outstanding} days`
    return row.due_date ? `${age} · due ${shortDate(row.due_date)}` : age
}

/** One section of an aged answer: the server's bucket (its count and amount over the whole aged set) over the rows drawn in it. */
interface AgingSection {
    label: string
    /** The bucket's rows over the whole aged set, from the server (the page's own unit). */
    count: number
    /** The bucket's amount due over the whole aged set; absent when the answer carried no buckets. */
    totalLabel?: string
    rows: TmsInvoiceRow[]
}

/** The bucket a row falls in: the first whose upper bound holds its days. A row below the first bucket, or with no age, falls in the first. */
const sectionIndexOf = (days: number | null, buckets: TmsInvoiceAgingBucket[]): number => {
    if (days === null) return 0
    const index = buckets.findIndex((bucket) => bucket.max_days === null || days <= bucket.max_days)
    return index < 0 ? buckets.length - 1 : index
}

/**
 * The aged answer's sections: one per server bucket that holds rows, headed by the bucket's count and amount over the whole aged set.
 * Without the server's buckets the rows are one section headed by how many are shown, with no totals.
 */
export const agingSectionsOf = (rows: TmsInvoiceRow[], buckets?: TmsInvoiceAgingBucket[]): AgingSection[] => {
    if (!buckets?.length) return rows.length ? [{ label: 'Aged invoices', count: rows.length, rows }] : []
    const groups: TmsInvoiceRow[][] = buckets.map(() => [])
    for (const row of rows) groups[sectionIndexOf(row.days_outstanding, buckets)].push(row)
    return buckets
        .map((bucket, i) => ({ label: bucket.label, count: bucket.invoice_count, totalLabel: formatUsd(bucket.total_usd), rows: groups[i] }))
        .filter((section) => section.count > 0)
}
